/** @jsxImportSource @emotion/react */
import React, { memo } from 'react';
import { css } from '@emotion/react';
import { useTheme } from '../provider/useTheme';
import type { IconSizeToken, IconSizeFullToken } from '../sizes/layout';
import type { SemanticColorToken } from '../themes/types';
import { icons } from '../icons';
import type { IconName } from '../icons';
import { getLoggerSafe } from '@vitvio/logging';

export interface IconProps {
  icon: IconName | null;
  size?: IconSizeToken;
  color?: SemanticColorToken;
}

export type IconSize = IconSizeToken;

export const CoreIcon: React.FC<IconProps> = memo(({ icon, size = 'md', color = 'fg' }) => {
  const { getColor, sizes } = useTheme();

  if (icon === null) return null;

  const svgRaw = icons[icon];

  if (!svgRaw) {
    getLoggerSafe()?.warn(`[CoreIcon] Unknown icon: "${icon}"`);
    return null;
  }

  const fullSizeToken: IconSizeFullToken = `iconSize.${size}`;
  const dimension = sizes?.layout?.[fullSizeToken] ?? 0;

  return (
    <span
      css={css`
        width: ${dimension}px;
        height: ${dimension}px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        color: ${getColor(color)};
      `}
      dangerouslySetInnerHTML={{ __html: svgRaw }}
    />
  );
});

CoreIcon.displayName = 'CoreIcon';

export default CoreIcon;
